/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.24.0
 */

import { pageApi } from '@/main/api/page.api';
import {
    API_NOTIFICATIONS_DELETE,
    API_NOTIFICATIONS_FEEDBACK,
    API_NOTIFICATIONS_LIST,
    API_NOTIFICATIONS_SETTINGS,
    API_NOTIFICATIONS_SHOWN,
    API_NOTIFICATIONS_UPDATE,
} from '@/main/modules/app/Api';
import { invalidateOnSuccess } from '@/main/utils/invalidateOnSuccess';

import { userNotificationsTags } from '../app/apiTags';
import { EHttpMethod } from '../app/appConstants';
import { setNotifications, setNotificationsSettings } from './slice';
import {
    TNotificationDeleteRequest,
    TNotificationUpdateRequest,
    TNotificationsSettings,
    TNotificationShownRequest,
    INotificationsList,
    TNotificationsFeedback,
} from './types';

export const userNotificationsApi = pageApi.injectEndpoints({
    endpoints: (builder) => ({
        getNotificationsSettings: builder.query<TNotificationsSettings, void>({
            query: () => ({
                url: API_NOTIFICATIONS_SETTINGS,
            }),
            providesTags: [userNotificationsTags.USER_NOTIFICATIONS_SETTINGS],
            // Не кешировать
            keepUnusedDataFor: 0,
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    /* Данные хранятся в slice */
                    dispatch(setNotificationsSettings(data));
                } catch (err) {
                    console.error(err);
                }
            },
        }),
        updateNotificationsSettings: builder.mutation<void, TNotificationsSettings>({
            query: (arg) => ({
                url: API_NOTIFICATIONS_SETTINGS,
                body: arg,
                method: EHttpMethod.PUT,
            }),
            invalidatesTags: (_result, error) =>
                invalidateOnSuccess(error, [
                    userNotificationsTags.USER_NOTIFICATIONS_SETTINGS,
                    userNotificationsTags.USER_NOTIFICATIONS_LIST,
                ]),
        }),
        getNotificationsList: builder.query<INotificationsList, void>({
            query: () => ({
                url: API_NOTIFICATIONS_LIST,
            }),
            providesTags: [userNotificationsTags.USER_NOTIFICATIONS_LIST],
            // Не кешировать
            keepUnusedDataFor: 0,
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    /* Данные хранятся в slice */
                    dispatch(setNotifications(data));
                } catch (err) {
                    console.error(err);
                }
            },
        }),
        updateNotification: builder.mutation<void, TNotificationUpdateRequest>({
            query: (arg) => ({
                url: API_NOTIFICATIONS_UPDATE,
                body: arg,
                method: EHttpMethod.PUT,
            }),
            invalidatesTags: (_result, error) =>
                invalidateOnSuccess(error, [userNotificationsTags.USER_NOTIFICATIONS_LIST]),
        }),
        deleteNotification: builder.mutation<void, TNotificationDeleteRequest>({
            query: (arg) => ({
                url: API_NOTIFICATIONS_DELETE,
                body: arg,
                method: EHttpMethod.DELETE,
            }),
            invalidatesTags: (_result, error) =>
                invalidateOnSuccess(error, [userNotificationsTags.USER_NOTIFICATIONS_LIST]),
        }),
        shownNotification: builder.mutation<void, TNotificationShownRequest>({
            query: (arg) => ({
                url: API_NOTIFICATIONS_SHOWN,
                body: arg,
                method: EHttpMethod.PUT,
            }),
        }),
        sendNotificationFeedback: builder.mutation<void, TNotificationsFeedback>({
            query: (arg) => ({
                url: API_NOTIFICATIONS_FEEDBACK,
                body: arg,
                method: EHttpMethod.PUT,
            }),
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetNotificationsSettingsQuery,
    useUpdateNotificationsSettingsMutation,
    useGetNotificationsListQuery,
    useUpdateNotificationMutation,
    useDeleteNotificationMutation,
    useShownNotificationMutation,
    useSendNotificationFeedbackMutation,
} = userNotificationsApi;
